import { ID, Models } from 'appwrite';
import { getAppwriteAccount, getAppwriteClient } from './appwriteClient';
import { withTimeout } from './asyncTimeout';
import { submitTrace } from './submitPipelineTrace';

const APPWRITE_AUTH_TIMEOUT_MS = 15_000;

let sessionUserFlight: Promise<AppwriteCompatUser | null> | null = null;
let jwtRefreshFlight: Promise<string | null> | null = null;

const JWT_STORAGE_KEY = 'appwrite_jwt';
const LEGACY_TOKEN_KEY = 'firebase_token';

/**
 * Embedded/sandboxed documents can expose `window` while denying access to
 * the Storage getter itself. Keep auth usable in that context with an
 * in-memory request path instead of turning a browser policy into an
 * unhandled SecurityError.
 */
const getBrowserStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export type AppwriteCompatUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerification: boolean;
};

const mapUser = (user: Models.User<Models.Preferences>): AppwriteCompatUser => ({
  uid: user.$id,
  email: user.email ?? null,
  displayName: user.name ?? user.email?.split('@')[0] ?? null,
  emailVerification: user.emailVerification === true,
});

const persistJwt = (jwt: string | null) => {
  const storage = getBrowserStorage();
  if (!storage) return;
  try {
    if (jwt) {
      storage.setItem(JWT_STORAGE_KEY, jwt);
      storage.setItem(LEGACY_TOKEN_KEY, jwt);
    } else {
      storage.removeItem(JWT_STORAGE_KEY);
      storage.removeItem(LEGACY_TOKEN_KEY);
    }
  } catch {
    // Private/sandboxed contexts may deny Storage operations after the getter.
  }
};

export const getStoredAppwriteJwt = (): string | null => {
  const storage = getBrowserStorage();
  if (!storage) return null;
  try {
    return storage.getItem(JWT_STORAGE_KEY) || storage.getItem(LEGACY_TOKEN_KEY);
  } catch {
    return null;
  }
};

/** Appwrite JWTs are short-lived; treat as stale before expiry. */
const decodeJwtExpiryMs = (jwt: string): number | null => {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = JSON.parse(atob(padded)) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isAppwriteJwtFresh = (jwt: string, skewMs = 120_000): boolean => {
  const expMs = decodeJwtExpiryMs(jwt);
  if (!expMs) return false;
  return expMs - Date.now() > skewMs;
};

export const refreshAppwriteJwt = async (): Promise<string | null> => {
  if (jwtRefreshFlight) {
    submitTrace('auth_jwt', 'await_inflight');
    return jwtRefreshFlight;
  }
  jwtRefreshFlight = (async () => {
    submitTrace('auth_jwt');
    const account = getAppwriteAccount();
    try {
      const { jwt } = await withTimeout(
        account.createJWT(),
        APPWRITE_AUTH_TIMEOUT_MS,
        'Appwrite JWT',
      );
      persistJwt(jwt);
      return jwt;
    } catch (error) {
      persistJwt(null);
      submitTrace('auth_fail', error instanceof Error ? error.message : 'createJWT failed');
      return null;
    }
  })().finally(() => {
    jwtRefreshFlight = null;
  });
  return jwtRefreshFlight;
};

export const getAppwriteSessionUser = async (): Promise<AppwriteCompatUser | null> => {
  if (sessionUserFlight) {
    submitTrace('auth_session', 'await_inflight');
    return sessionUserFlight;
  }
  sessionUserFlight = (async () => {
    submitTrace('auth_session');
    try {
      const user = await withTimeout(
        getAppwriteAccount().get(),
        APPWRITE_AUTH_TIMEOUT_MS,
        'Appwrite session',
      );
      return mapUser(user);
    } catch (error) {
      submitTrace('auth_fail', error instanceof Error ? error.message : 'session failed');
      return null;
    }
  })().finally(() => {
    sessionUserFlight = null;
  });
  return sessionUserFlight;
};

const isActiveSessionError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error && 'message' in error
        ? String((error as { message?: unknown }).message)
        : String(error);
  return /session is active|session is prohibited|user_session_already_exists/i.test(
    message,
  );
};

/** Drop browser cookie sessions so email login can create a fresh one. */
export const clearAppwriteSessions = async (): Promise<void> => {
  const account = getAppwriteAccount();
  try {
    await account.deleteSessions();
  } catch {
    try {
      await account.deleteSession('current');
    } catch {
      // no active session
    }
  }
  persistJwt(null);
};

export const signInWithAppwriteEmail = async (
  email: string,
  password: string,
): Promise<string> => {
  const account = getAppwriteAccount();
  const normalizedEmail = email.trim();

  const existing = await getAppwriteSessionUser();
  if (existing?.email?.toLowerCase() === normalizedEmail.toLowerCase()) {
    const jwt = await refreshAppwriteJwt();
    if (jwt) {
      return jwt;
    }
  }

  await clearAppwriteSessions();

  try {
    await account.createEmailPasswordSession(normalizedEmail, password);
  } catch (error) {
    if (!isActiveSessionError(error)) {
      throw error;
    }
    await clearAppwriteSessions();
    await account.createEmailPasswordSession(normalizedEmail, password);
  }

  const profile = await account.get();
  if (profile.emailVerification !== true) {
    await clearAppwriteSessions();
    throw new Error('EMAIL_NOT_VERIFIED');
  }

  const jwt = await refreshAppwriteJwt();
  if (!jwt) {
    throw new Error('Appwrite session created but JWT was not issued');
  }
  return jwt;
};

export const signUpWithAppwriteEmail = async (
  email: string,
  password: string,
): Promise<string> => {
  const account = getAppwriteAccount();
  const normalizedEmail = email.trim();
  await account.create(ID.unique(), normalizedEmail, password);
  await clearAppwriteSessions();
  try {
    await account.createEmailPasswordSession(normalizedEmail, password);
    await sendAppwriteEmailVerification();
  } finally {
    await clearAppwriteSessions();
  }
  throw new Error('CHECK_EMAIL');
};

export const signOutAppwrite = async (): Promise<void> => {
  await clearAppwriteSessions();
  getAppwriteClient();
};

export const requestAppwritePasswordRecovery = async (email: string): Promise<void> => {
  const redirect = `${window.location.origin}/reset-password`;
  await getAppwriteAccount().createRecovery(email.trim(), redirect);
};

export const completeAppwritePasswordRecovery = async (
  userId: string,
  secret: string,
  password: string,
): Promise<void> => {
  await getAppwriteAccount().updateRecovery(userId, secret, password);
};

const emailVerificationRedirect = (): string => {
  if (typeof window === 'undefined') return 'https://pixlys.com/verify-email';
  return `${window.location.origin}/verify-email`;
};

export const completeAppwriteEmailVerification = async (
  userId: string,
  secret: string,
): Promise<void> => {
  await getAppwriteAccount().updateVerification(userId, secret);
};

export const sendAppwriteEmailVerification = async (): Promise<void> => {
  await getAppwriteAccount().createVerification(emailVerificationRedirect());
};

/** Sends a new verification email; invalidates previous links — use only on explicit user action. */
export const resendAppwriteEmailVerification = async (
  email: string,
  password: string,
): Promise<void> => {
  const account = getAppwriteAccount();
  const normalizedEmail = email.trim();
  await clearAppwriteSessions();
  try {
    await account.createEmailPasswordSession(normalizedEmail, password);
    await sendAppwriteEmailVerification();
  } finally {
    await clearAppwriteSessions();
  }
};

const signInWithAppwriteProvider = async (
  provider: 'google' | 'apple',
  successUrl?: string,
  failureUrl?: string,
): Promise<void> => {
  const success = successUrl || `${window.location.origin}/dashboard`;
  const failure = failureUrl || `${window.location.origin}/login`;
  await getAppwriteAccount().createOAuth2Session(provider, success, failure);
};

export const signInWithAppwriteGoogle = async (
  successUrl?: string,
  failureUrl?: string,
): Promise<void> => signInWithAppwriteProvider('google', successUrl, failureUrl);

export const signInWithAppwriteApple = async (
  successUrl?: string,
  failureUrl?: string,
): Promise<void> => signInWithAppwriteProvider('apple', successUrl, failureUrl);

export const getAppwriteJwtForApi = async (forceRefresh = false): Promise<string | null> => {
  const stored = !forceRefresh ? getStoredAppwriteJwt() : null;
  if (stored && isAppwriteJwtFresh(stored)) {
    submitTrace('auth_cache_hit');
    return stored;
  }
  return refreshAppwriteJwt();
};
